import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseSessionStorage<T> {
  value: T;
  setValue: Dispatch<SetStateAction<T>>;
  removeValue: () => void;
}

export const enum SessionStorageKey {
  CHANNEL_FOCUS_FILTERS = 'CHANNEL_FOCUS_FILTERS',
  CHANNEL_FOCUS_SECONDARY_SEGMENT_FILTERS = 'CHANNEL_FOCUS_SECONDARY_SEGMENT_FILTERS',
  AGGREGATED_CONSENT_FILTERS = 'AGGREGATED_CONSENT_FILTERS',
  GLOBAL_BENCHMARKING_FILTERS = 'GLOBAL_BENCHMARKING_FILTERS',
  GLOBAL_FILTERS = 'GLOBAL_FILTERS',
  GLOBAL_FILTER_OPTION_PATHS = 'GLOBAL_FILTER_OPTION_PATHS',
  LOCAL_FILTER_OPTION_PATHS = 'LOCAL_FILTER_OPTION_PATHS',
  PARTNER_SCORE_FILTERS = 'PARTNER_SCORE_FILTERS',
  COMPETITOR_ANALYSIS_FILTERS = 'COMPETITOR_ANALYSIS_FILTERS',
  INSIGHTS_FILTERS = 'INSIGHTS_FILTERS',
  NETWORK_CONNECTION_FILTERS = 'NETWORK_CONNECTION_FILTERS',
  SALES_FILTERS = 'SALES_FILTERS',
}

export const SESSION_STORAGE_SYNC_EVENT = 'session-storage-sync';

/**
 * Writes a value to sessionStorage and dispatches a sync event
 * so any mounted useSessionStorage hooks re-read the value.
 */
export const syncSessionStorage = (storageKey: string, value: unknown): void => {
  sessionStorage.setItem(storageKey, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key: storageKey } }));
};

export const clearSessionStorage = (storageKey: string): void => {
  sessionStorage.removeItem(storageKey);
  window.dispatchEvent(new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key: storageKey } }));
};

/**
 * Applies the structural checks and optional `validate` guard to an
 * already-parsed value, narrowing it to `T` or returning the fallback.
 */
function narrowParsedValue<T>(parsed: unknown, fallbackValue: T, validate?: (v: unknown) => v is T): T {
  if (parsed === null || parsed === undefined) return fallbackValue;
  if (typeof fallbackValue === 'object' && typeof parsed !== 'object') return fallbackValue;
  if (validate) return validate(parsed) ? parsed : fallbackValue;
  // Without a validate guard, T is unbounded; the structural checks above are
  // the best-effort narrowing. Prefer passing a validate guard for full safety.
  return parsed as T;
}

/**
 * Parses a JSON string, with the same structural checks and optional `validate`
 * guard as {@link parseStoredValue} but without any sessionStorage access.
 */
export function parseJsonValue<T>(raw: string | null, fallbackValue: T, validate?: (v: unknown) => v is T): T {
  if (!raw) return fallbackValue;
  try {
    return narrowParsedValue(JSON.parse(raw), fallbackValue, validate);
  } catch {
    return fallbackValue;
  }
}

export function parseStoredValue<T>(key: string, fallbackValue: T, validate?: (v: unknown) => v is T): T {
  const item = sessionStorage.getItem(key);
  if (!item) return fallbackValue;
  try {
    return narrowParsedValue(JSON.parse(item), fallbackValue, validate);
  } catch {
    sessionStorage.removeItem(key);
    return fallbackValue;
  }
}

const useSessionStorage = <T>(key: SessionStorageKey | string, fallbackValue: T): UseSessionStorage<T> => {
  const [value, setValue] = useState<T>(() => parseStoredValue(key, fallbackValue));

  const firstRun = useRef(true);
  const isRemoving = useRef(false);
  const fallbackRef = useRef(fallbackValue);
  fallbackRef.current = fallbackValue;

  // Listen for external writes via syncSessionStorage / clearSessionStorage.
  // Events from the hook's own write-back effect carry source:'hook' and are
  // ignored here — they exist only to notify non-hook listeners (e.g.
  // useActiveFilterBar). Without this filter, multiple hook instances sharing
  // the same key would ping-pong state updates indefinitely.
  useEffect(() => {
    const handleSync = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const detail = e.detail;
      if (detail.key !== key) return;
      if (detail.source === 'hook') return;
      const item = sessionStorage.getItem(key);
      if (item) {
        setValue(parseStoredValue(key, fallbackRef.current));
      }
    };
    window.addEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
  }, [key]);

  useEffect(() => {
    if (isRemoving.current) {
      isRemoving.current = false;
      return;
    }

    if (!firstRun.current) {
      if (value !== undefined && value !== null) {
        sessionStorage.setItem(key, JSON.stringify(value));
      } else {
        sessionStorage.removeItem(key);
      }
      // Notify non-hook listeners (e.g. useActiveFilterBar). source:'hook'
      // tells other useSessionStorage instances to ignore this event.
      window.dispatchEvent(new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key, source: 'hook' } }));
    } else {
      firstRun.current = false;
    }
  }, [key, value]);

  const removeValue = useCallback(() => {
    isRemoving.current = true;
    sessionStorage.removeItem(key);
    setValue(fallbackValue);
  }, [key, fallbackValue]);

  return useMemo(
    () => ({
      value,
      setValue,
      removeValue,
    }),
    [value, setValue, removeValue],
  );
};

export default useSessionStorage;
